import { Button, Drawer, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { newIdempotencyKey } from "../../api/client";
import { addPunch, editDay, type AttendanceDay, type DayStatus } from "../../api/attendance";
import { useServices } from "../../app/services";
import { Field, FormError, messageOf, styles as c, useScope } from "../classes/common";
import { zonedToEpoch } from "../classes/time";

export const STATUS_LABEL: Record<string, string> = { present: "Present", late: "Late", half_day: "Half day", absent: "Absent", leave: "Leave", holiday: "Holiday", off: "Off" };
export const STATUS_CODE: Record<string, string> = { present: "P", late: "L", half_day: "½", absent: "A", leave: "LV", holiday: "H", off: "–" };
export const EDITABLE: DayStatus[] = ["present", "half_day", "absent", "leave", "holiday", "off"];
/** What the cell shows: the API's status, except a present day flagged late reads as Late. No hours are computed here. */
export const cellKind = (d: Pick<AttendanceDay, "status" | "late"> | null | undefined): string => (!d ? "none" : d.status === "present" && d.late ? "late" : d.status);

export function DayEditDrawer({ staffId, name, date, current, onClose }: { staffId: string; name: string; date: string; current: AttendanceDay | null; onClose: () => void }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<DayStatus>(current?.status ?? "present");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => editDay(client, staffId, date, { status, reason: reason.trim() }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["attendance"] }); toast.show("Day updated"); onClose(); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    if (reason.trim().length < 3) return setError("Say why you are changing this day (at least 3 characters).");
    setError(null);
    save.mutate();
  };
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title={`Edit ${date}`} description={name}
      footer={<div className={c.row}><Button variant="primary" busy={save.isPending} onClick={submit}>Save day</Button><Button variant="plain" onClick={onClose}>Cancel</Button></div>}>
      <form className={`${c.stack} ${c.drawerPad}`} onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        {current?.overrideReason ? <p className={c.note}>Last changed: {current.overrideReason}</p> : null}
        <Field label="Status" htmlFor="day-status"><select id="day-status" className={c.input} value={status} onChange={(e) => setStatus(e.target.value as DayStatus)}>{EDITABLE.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select></Field>
        <Field label="Reason" htmlFor="day-reason" hint="Kept in the audit trail."><textarea id="day-reason" className={`${c.input} ${c.area}`} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <FormError message={error} />
      </form>
    </Drawer>
  );
}

export function PunchDrawer({ staffId, name, homeBranchId, defaultDate, onClose }: { staffId: string; name: string; homeBranchId: string | null; defaultDate: string; onClose: () => void }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const key = useRef(newIdempotencyKey());
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("09:00");
  const [direction, setDirection] = useState<"" | "in" | "out">("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  // The API falls back to the home branch; a provisioned owner has none, so name the branch in use.
  const branchId = homeBranchId ? undefined : sc.branchId ?? sc.ctx.me?.branch?.id;
  const save = useMutation({
    mutationFn: () => addPunch(client, staffId, { at: zonedToEpoch(date, time, sc.ctx.timeZone), ...(direction ? { direction } : {}), ...(branchId ? { branchId } : {}), reason: reason.trim() }, key.current),
    onSuccess: () => { key.current = newIdempotencyKey(); void qc.invalidateQueries({ queryKey: ["attendance"] }); toast.show("Punch added"); onClose(); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return setError("Choose the date and time of the missed punch.");
    if (date > sc.date) return setError("A punch cannot be in the future.");
    if (reason.trim().length < 3) return setError("Say why you are adding this punch (at least 3 characters).");
    setError(null);
    save.mutate();
  };
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title="Add a missed punch" description={`${name}. Times are on the gym's clock (${sc.ctx.timeZone}).`}
      footer={<div className={c.row}><Button variant="primary" busy={save.isPending} onClick={submit}>Add punch</Button><Button variant="plain" onClick={onClose}>Cancel</Button></div>}>
      <form className={`${c.stack} ${c.drawerPad}`} onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        <div className={c.row}>
          <Field label="Date" htmlFor="pn-date"><input id="pn-date" type="date" className={c.input} value={date} max={sc.date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Time" htmlFor="pn-time"><input id="pn-time" type="time" className={c.input} value={time} onChange={(e) => setTime(e.target.value)} /></Field>
        </div>
        <Field label="Direction" htmlFor="pn-dir" hint="Leave on automatic and the API picks in or out."><select id="pn-dir" className={c.input} value={direction} onChange={(e) => setDirection(e.target.value as "" | "in" | "out")}><option value="">Automatic</option><option value="in">In</option><option value="out">Out</option></select></Field>
        <Field label="Reason" htmlFor="pn-reason"><textarea id="pn-reason" className={`${c.input} ${c.area}`} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <FormError message={error} />
      </form>
    </Drawer>
  );
}
